import type {
  Hit,
  MultipleQueriesQuery,
  SearchOptions,
  SearchResponse,
} from '@algolia/client-search';
import algoliasearch, { SearchClient } from 'algoliasearch';
import { Hit as InstantSearchHit } from 'instantsearch.js';

import { getConfigEntry } from '@/api/config';
import { ALGOLIA_USER_TOKEN_COOKIE } from '@/lib/personalization/common';
import { ProductVariantSimplifierPrice } from '@/lib/product/prices';
import { trackAlgoliaExperiment } from '@/utils/analytics/experimentViewedEvent';
import { getCookie } from '@/utils/isomorphic/cookie';

export const CATEGORIES_INDEX = 'Departments'; // Legacy index name from webstore terminology
export const PRODUCTS_INDICES = [
  'Products',
  'ProductsByPriceDesc',
  'ProductsByPriceAsc',
  'ProductsByActivatedAtDesc',
];
type ProductsIndex = (typeof PRODUCTS_INDICES)[number];
export const PRODUCTS_INDEX: ProductsIndex = 'Products';
const SUGGESTIONS_INDEX = 'Products_query_suggestions';

let clientSingleton: SearchClient | undefined;

export const init = () => {
  if (clientSingleton) return clientSingleton;
  const { app, key } = getConfigEntry('algolia');
  clientSingleton = algoliasearch(app, key);
  return clientSingleton;
};

export function getIndex(name: string) {
  return init().initIndex(name);
}

export interface CategoryQueryOptions extends SearchOptions {
  categoryKey: string;
}

interface BaseAlgoliaCategory {
  objectID: string;
  name: string;
  idealForSuggestions: boolean;
  parentKey: string;
  path: string;
  url: string;
  description: string;
  image_url: string;
  depth: number;
  parentId: number;
  parentName?: string;
  quantities: {
    products: number;
    subcategories: number;
    tags: number;
  };
  orderHint: number;
  queryID?: string;
}
export interface AlgoliaCategory extends BaseAlgoliaCategory {
  type: 'department';
}

export interface SynthesizedAlgoliaCategory extends BaseAlgoliaCategory {
  total: number;
  type: 'synthesizedDepartment';
}

export interface AlgoliaProduct {
  ancestryPairs: string[];
  categoryKeys: string[];
  /**
   * Formatted mapping of taxonomy lineages that contain this product. Supports hierarchical
   * refinement of products by category. Currently reaches at least `lvl3` in practice for some
   * products, may exceed. For now, we only use `lvl0` and `lvl1` for refinement.
   *
   * Uses a ` > ` separator for each level, e.g. `Chocolates & Sweets > Nuts`.
   */
  categoryNames: {
    lvl0: string[];
    lvl1?: string[];
  };
  /**
   * See {@link AlgoliaProduct.categoryNames} for format details.
   *
   * New category hierarchy added to support customizers categories.
   */
  customizerCategoryNames?: {
    lvl0: string[];
    lvl1?: string[];
  };
  density?: number;
  facets: {
    [facetName: string]: string[];
  };
  key: string;
  listingImageUrl: string;
  merchandisingCategory?: string;
  name: string;
  path: string;
  reportingCategory?: string;
  requiresCustomization: boolean;
  reviews: {
    averageRating?: number;
    count: number;
    totalRatings: number;
    totalReviews: number;
  };
  searchableTags: string[];
  shortDescription: string;
  tags: string[];
}

interface SuggestionOriginInsights {
  exact_nb_hits: number;
  facets: {
    analytics: Record<string, any>;
    exact_matches: Record<string, any>;
  };
}
export interface AlgoliaSuggestedSearch {
  Departments?: SuggestionOriginInsights;
  nb_words: number;
  objectID: string;
  popularity: number;
  Products?: SuggestionOriginInsights;
  query: string;
  queryID?: string;
}

export interface BaseAlgoliaVariant {
  autoDeliveryEligible: boolean;
  bulk?: boolean;
  comparisonPrice?: number;
  cost: string;
  discountPercent?: number;
  displayPriceByChannel: Record<'default' | string, string>;
  formattedUnitPrice: string;
  hasSiblings?: boolean;
  outOfStock: boolean;
  margin: number;
  path: string;
  popularity: number;
  preferFormattedUnitPrice: boolean;
  prices: ProductVariantSimplifierPrice[];
  Product: AlgoliaProduct;
  Product_activatedAt: number;
  Product_allVariantsOutOfStock: boolean;
  promoteShortVariantName: boolean;
  queryID?: string;
  shortVariantName: string;
  singlePiecePrice: number;
  singlePiecePriceByChannel: Record<'default' | string, number>;
  sku: string;
  substituteVariant?: {
    prices: ProductVariantSimplifierPrice[];
    shortVariantName: string;
    sku: string;
    variantLabel?: string;
    weight: string;
  };
  traits: string[];
  variantLabel?: string;
  warehouses: string[];
  weight: string;
  wholesale?: boolean;
}
type MaybeInstantSearchHit = Partial<Pick<InstantSearchHit, '__position' | '__queryID'>>;
export type AlgoliaVariant = MaybeInstantSearchHit & Hit<BaseAlgoliaVariant>;

export const parseResponse = <T extends { queryID?: string }>(response: SearchResponse<T>) => {
  if (!response.queryID) return response.hits;

  return response.hits.map((h) => ({ ...h, queryID: response.queryID }));
};

export function categorySearchParams(options: SearchOptions) {
  const { query, ...searchOptions } = options;
  return {
    query,
    params: {
      ...searchOptions,
      analyticsTags: searchOptions.analyticsTags ?? ['Search Categories'],
      getRankingInfo: true,
      hitsPerPage: searchOptions.hitsPerPage ?? 50,
      userToken: getCookie(ALGOLIA_USER_TOKEN_COOKIE, false),
    },
  };
}

export function productSearchParams(options: SearchOptions) {
  const { query, ...searchOptions } = options;
  return {
    query,
    params: {
      ...searchOptions,
      analyticsTags: searchOptions.analyticsTags ?? ['Search Products'],
      getRankingInfo: true,
      hitsPerPage: searchOptions.hitsPerPage ?? 50,
      userToken: getCookie(ALGOLIA_USER_TOKEN_COOKIE, false),
    },
  };
}

export async function browseProducts(options: SearchOptions) {
  let hits: AlgoliaVariant[] = [];
  await getIndex(PRODUCTS_INDEX).browseObjects<BaseAlgoliaVariant>({
    ...options,
    batch: (batch) => {
      hits = hits.concat(batch);
    },
  });
  return hits;
}

export async function searchCategories<
  T extends BaseAlgoliaCategory = AlgoliaCategory | SynthesizedAlgoliaCategory,
>(options: SearchOptions) {
  const { query, params } = categorySearchParams(options);
  const response = await getIndex(CATEGORIES_INDEX).search<T>(query ?? '', params);

  trackAlgoliaExperiment(response);

  return {
    ...response,
    hits: parseResponse(response),
  };
}

export async function searchProducts(options: SearchOptions, index?: ProductsIndex) {
  const { query, params } = productSearchParams(options);
  const response = await getIndex(index ?? PRODUCTS_INDEX).search<BaseAlgoliaVariant>(
    query ?? '',
    params,
  );

  trackAlgoliaExperiment(response);

  return {
    ...response,
    hits: parseResponse(response),
  };
}

export async function searchSuggestions(options: SearchOptions) {
  const { query, ...searchOptions } = options;
  const response = await getIndex(SUGGESTIONS_INDEX).search<AlgoliaSuggestedSearch>(query ?? '', {
    ...searchOptions,
    analyticsTags: searchOptions.analyticsTags ?? ['Search Suggestions'],
    getRankingInfo: true,
    userToken: getCookie(ALGOLIA_USER_TOKEN_COOKIE, false),
  });

  trackAlgoliaExperiment(response);

  return {
    ...response,
    hits: parseResponse(response),
  };
}

export async function getChildCategories(options: CategoryQueryOptions) {
  const { categoryKey, ...searchOptions } = options;
  return searchCategories({
    ...searchOptions,
    analyticsTags: searchOptions.analyticsTags ?? ['Search Child Categories'],
    facetFilters: [`parentKey:${categoryKey}`],
    typoTolerance: false,
  });
}

export async function entryCountsForChildCategories(
  options: CategoryQueryOptions,
  index?: ProductsIndex,
) {
  const ancestorPairStart = `${options.categoryKey.replace('-', '')}subcat`;
  const { facetHits } = await getIndex(index ?? PRODUCTS_INDEX).searchForFacetValues(
    'Product.ancestryPairs',
    ancestorPairStart,
    {
      analyticsTags: options.analyticsTags ?? ['Search Entry Count Child Categories'],
      clickAnalytics: options.clickAnalytics,
      filters: options.filters ?? 'traits:primary',
      maxFacetHits: 100,
      ruleContexts: options.ruleContexts,
      typoTolerance: false,
    },
  );

  return facetHits.reduce<{ [objectID: string]: number }>((countByCategory, facetHit) => {
    if (facetHit.value.includes('subcat')) {
      return {
        ...countByCategory,
        [facetHit.value.split('subcat')[1]]: facetHit.count,
      };
    }
    return countByCategory;
  }, {});
}

/**
 * Fetch multiple queries in a single request.
 *
 * The type signature here is somewhat awkward because we want to support a heterogeneous array of
 * results; Algolia's `multipleQueries` type signature only allows for a homogeneous array.
 *
 * One benefit of this approach is that TS makes sure the number of arguments matches the number of
 * entries supplied to the generic array.
 */
export async function multiQuerySearch<
  T extends (AlgoliaSuggestedSearch | BaseAlgoliaCategory | BaseAlgoliaVariant)[],
>(
  ...queries: { [K in keyof T]: MultipleQueriesQuery }
): Promise<{ [K in keyof T]: SearchResponse<T[K]> }> {
  const client = init();
  const { results } = await client.multipleQueries(queries);

  results.forEach(trackAlgoliaExperiment);

  return results.map((response) => ({
    ...response,
    hits: parseResponse(response),
  })) as { [K in keyof T]: SearchResponse<T[K]> };
}

export function tagFacetFilter(tag: string) {
  return `Product.tags:${tag}`;
}
